import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { XMarkIcon, UserPlusIcon, UserGroupIcon, ClockIcon } from '@heroicons/react/24/outline';
import { projectSharingService } from '../../services/projectSharingService';

const TABS = [
  { key: 'invite',  label: 'Invite',  icon: UserPlusIcon },
  { key: 'members', label: 'Members', icon: UserGroupIcon },
  { key: 'history', label: 'History', icon: ClockIcon },
];

function statusBadge(status) {
  const map = {
    pending:                'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    pending_owner_approval: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    accepted:               'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    rejected:               'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
    owner_rejected:         'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
    cancelled:              'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  };
  return map[status] || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
}

function statusLabel(status) {
  return {
    pending:                'Pending',
    pending_owner_approval: 'Awaiting owner',
    accepted:               'Accepted',
    rejected:               'Rejected',
    owner_rejected:         'Owner rejected',
    cancelled:              'Cancelled',
  }[status] || status;
}

function actionLabel(action) {
  return {
    project_created:           'created the project',
    project_updated:           'updated the project',
    member_invited:            'invited a user',
    invitation_accepted:       'accepted an invitation',
    invitation_rejected:       'rejected an invitation',
    invitation_cancelled:      'cancelled an invitation',
    invitation_owner_approved: 'approved an invitation',
    invitation_owner_rejected: 'declined an invitation',
    member_removed:            'removed a member',
    member_left:               'left the project',
    developer_added:           'added a developer',
    developer_removed:         'removed a developer',
    developer_updated:         'updated a developer',
    capacity_updated:          'updated capacity',
    plan_estimate_updated:     'updated plan estimate',
  }[action] || action;
}

export default function ShareProjectModal({ project, onClose }) {
  const [activeTab, setActiveTab] = useState('invite');

  // Invite tab
  const [shareableUsers, setShareableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [inviting, setInviting] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Members tab
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [busyMemberId, setBusyMemberId] = useState(null);
  const [busyInvitationId, setBusyInvitationId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // { userId, displayName } | null
  // History tab
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  const loadInvite = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const users = await projectSharingService.getShareableUsers(project.id);
      setShareableUsers(users || []);
      setSelectedUserId('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingUsers(false);
    }
  }, [project.id]);

  const loadMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const [m, inv] = await Promise.all([
        projectSharingService.getMembers(project.id),
        projectSharingService.listProjectInvitations(project.id),
      ]);
      setMembers(m || []);
      setInvitations(inv || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingMembers(false);
    }
  }, [project.id]);

  const loadHistory = useCallback(async () => {
    setLoadingActivities(true);
    try {
      const data = await projectSharingService.getActivity(project.id);
      setActivities(data || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingActivities(false);
    }
  }, [project.id]);

  useEffect(() => {
    if (activeTab === 'invite')  loadInvite();
    if (activeTab === 'members') loadMembers();
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadInvite, loadMembers, loadHistory]);

  const handleInvite = async () => {
    if (!selectedUserId || inviting) return;
    setInviting(true);
    try {
      await projectSharingService.createInvitation(project.id, Number(selectedUserId));
      const isOwner = project.is_owner;
      toast.success(
        isOwner
          ? 'Invitation sent'
          : 'Invitation submitted for owner approval'
      );
      // Refresh the user list so the just-invited user disappears
      await loadInvite();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (invitationId) => {
    if (busyInvitationId === invitationId) return;
    setBusyInvitationId(invitationId);
    try {
      await projectSharingService.cancel(invitationId);
      toast.success('Invitation cancelled');
      await loadMembers();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyInvitationId(null);
    }
  };

  const handleRemoveMember = (userId, displayName) => {
    setConfirmRemove({ userId, displayName });
  };

  const confirmRemoveMember = async () => {
    if (!confirmRemove) return;
    const { userId } = confirmRemove;
    if (busyMemberId === userId) return;
    setBusyMemberId(userId);
    try {
      await projectSharingService.removeMember(project.id, userId);
      toast.success('Member removed');
      await loadMembers();
      setConfirmRemove(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyMemberId(null);
    }
  };

  const openInvitations = useMemo(
    () => invitations.filter((i) => i.status === 'pending' || i.status === 'pending_owner_approval'),
    [invitations]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded-xl bg-white dark:bg-slate-800 shadow-xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Share project
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-md" title={project.name}>
              {project.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors border-b-2 ${
                  active
                    ? 'border-indigo-500 text-indigo-700 dark:text-indigo-300'
                    : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                <Icon className="size-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {activeTab === 'invite' && (
            <div className="space-y-4">
              {!project.is_owner && (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
                  You're a member of this project. Invitations you send will be queued for owner approval before being delivered.
                </div>
              )}
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Select a user to invite
              </label>
              {loadingUsers ? (
                <p className="text-sm text-slate-500">Loading users...</p>
              ) : shareableUsers.length === 0 ? (
                <p className="text-sm text-slate-500">No users available to invite.</p>
              ) : (
                <>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">— Pick a user —</option>
                    {shareableUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.display_name ? `${u.display_name} (${u.email})` : u.email}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleInvite}
                    disabled={!selectedUserId || inviting}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-semibold text-white"
                  >
                    <UserPlusIcon className="size-4" />
                    {inviting ? 'Sending...' : 'Send invitation'}
                  </button>
                </>
              )}
            </div>
          )}

          {activeTab === 'members' && (
            <div className="space-y-6">
              <section>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Members</h3>
                {loadingMembers ? (
                  <p className="text-sm text-slate-500">Loading...</p>
                ) : members.length === 0 ? (
                  <p className="text-sm text-slate-500">No members yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-slate-700 rounded-lg border border-slate-200 dark:border-slate-700">
                    {members.map((m) => (
                      <li key={m.id} className="flex items-center justify-between px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {m.user.display_name || m.user.email}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{m.user.email}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            m.role === 'owner'
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                              : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                          }`}>
                            {m.role}
                          </span>
                          {m.role !== 'owner' && project.is_owner && (
                            <button
                              type="button"
                              disabled={busyMemberId === m.user.id}
                              onClick={() => handleRemoveMember(m.user.id, m.user.display_name || m.user.email)}
                              className="text-xs text-rose-600 hover:text-rose-700 dark:text-rose-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {busyMemberId === m.user.id ? 'Removing...' : 'Remove'}
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Open invitations</h3>
                {openInvitations.length === 0 ? (
                  <p className="text-sm text-slate-500">No open invitations.</p>
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-slate-700 rounded-lg border border-slate-200 dark:border-slate-700">
                    {openInvitations.map((inv) => (
                      <li key={inv.id} className="flex items-center justify-between px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {inv.invitee.display_name || inv.invitee.email}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            Invited by {inv.inviter.display_name || inv.inviter.email}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(inv.status)}`}>
                            {statusLabel(inv.status)}
                          </span>
                          <button
                            type="button"
                            disabled={busyInvitationId === inv.id}
                            onClick={() => handleCancelInvite(inv.id)}
                            className="text-xs text-rose-600 hover:text-rose-700 dark:text-rose-400 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {busyInvitationId === inv.id ? 'Cancelling...' : 'Cancel'}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              {loadingActivities ? (
                <p className="text-sm text-slate-500">Loading...</p>
              ) : activities.length === 0 ? (
                <p className="text-sm text-slate-500">No activity yet.</p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                  {activities.map((a) => (
                    <li key={a.id} className="py-3">
                      <p className="text-sm text-slate-800 dark:text-slate-200">
                        <span className="font-medium">{a.user.display_name || a.user.email}</span>{' '}
                        {actionLabel(a.action)}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {new Date(a.created_at).toLocaleString()}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {confirmRemove && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-xl bg-white dark:bg-slate-800 shadow-xl p-5">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                Remove member
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Remove <span className="font-medium">{confirmRemove.displayName}</span> from this project?
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmRemove(null)}
                  disabled={busyMemberId === confirmRemove.userId}
                  className="px-3 py-1.5 text-sm rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmRemoveMember}
                  disabled={busyMemberId === confirmRemove.userId}
                  className="px-3 py-1.5 text-sm rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busyMemberId === confirmRemove.userId ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}